/*
 * axios配置  可自行根据项目进行更改，只需更改该文件即可，其他文件可以不动
 */

import type { AxiosResponse } from 'axios'
import type { RequestOptions, Result } from './type'
import type { AxiosTransform, CreateAxiosOptions } from './axiosTransform'
import { VAxios } from './Axios'
import { RequestEnum, ResultEnum, ContentTypeEnum } from './httpEnum'
import { isString } from '../../is'
import { joinTimestamp, formatRequestDate, changeHump } from './helper'
import { AxiosRetry } from './axiosRetry'
import axios from 'axios'
import { isFunction } from '../../is'
import generateRequestSign from '../../../functions/generateRequestSign'

/**
 * @description: 数据处理，方便区分多种处理方式
 */
const transform: AxiosTransform = {
    /**
     * @description: 请求拦截器处理
     */
    requestInterceptors: (config) => {
        const timestamp = +new Date()
        /*
         * if (config.data) {
         *     config.data.bizId  = 1
         *     config.data.userId = 8
         * }
         */
        let sign = generateRequestSign(config.data, config.method, timestamp)
        if (config.headers['Content-Type'] === 'multipart/form-data') {
            const params = {}
            for (const [key, value] of config.data.entries()) {
                if (Object.prototype.toString.call(value) == '[object File]') {
                    continue
                }
                params[key] = value
            }
            sign = generateRequestSign(params, 'get', timestamp)
        }
        const btoken = localStorage.getItem('btoken')
        config.headers['B-Sign'] = sign
        config.headers['B-Timestamp'] = timestamp
        if (btoken) {
            config.headers['B-Token'] = btoken
        }
        return config
    },

    /**
     * @description: 响应拦截器处理
     */
    responseInterceptors: (res: AxiosResponse<any>) => {
        const type: string = res.data?.type
        // 如果返回的是blob类型的图片
        if (type?.indexOf('image') > -1) {
            return Promise.resolve(res.data)
        }
        let data = res.data
        const code = data.code
        if (typeof data === 'string') {
            data = JSON.parse(data)
        }
        else if (code === 200) {
            return data
        }
        else {
            // 需要自定义 reject 处理，就直接 return ，让业务自己处理业务
            if (res.config.headers && res.config.headers['custom-reject']) {
                return Promise.reject(data)
            }
            /*
             *  未登录状态或者token过期
             * code 10300表示用户请求参数错误，如果跳登录页，会导致后端一报错直接回登录页面
             */
            if (code === 10202 || code === 10203 || code === 10201) {
                const location = window.location
                const url = location.href
                window.location.href = location.protocol + '//'
                    + location.host + '/login?service=' + encodeURIComponent(url)
                return Promise.reject(data)
            }
            // 不弹toast的错误码，需自己处理
            if (
                code === 2000010086
            ) {
                return Promise.reject(data)
            }
            // 黑名单拦截
            if (code === 10206) {
                window.location.href = '/pages/unauthorized.html?code=10206'
                return Promise.reject(data)
            }
            window.toast(data.msg || '系统异常', 'error')
            return Promise.reject(data)
        }
    },

    /**
     * @description: 响应错误处理
     */
    responseInterceptorsCatch: (error: any) => {
        const response = error.response
        const data = response.data
        if (error.message) {
            console.error(error.message)
        }
        // 需要自定义 reject 处理，就直接 return ，让业务自己处理业务
        if (response.config.headers && response.config.headers['custom-reject']) {
            return Promise.reject(data)
        }
        const status = error.response.status
        // 401 表明没有权限，跳转到登录页，与 status 200 code为1000000500 逻辑类似
        if (status === 401) {
            const url = window.location.href
            const redirectUrl = data?.data?.redirectUrl
            if (redirectUrl) {
                window.location.href = data?.data?.redirectUrl + encodeURIComponent(url)
            }
            else {
                window.location.href = data?.data?.redirectUrl || '/login?url=' + encodeURIComponent(url)
            }
            return Promise.reject(error)
        }
        if (!response) {
            window.toast('无网络链接，请检查后重试', 'error')
        }
        else {
            window.toast(data && data.msg ? data.msg : '系统异常', 'error')
        }
        return Promise.reject(error)
    }
}

function createAxios() {
    return new VAxios({
        /*
         * See https://developer.mozilla.org/en-US/docs/Web/HTTP/Authentication#authentication_schemes
         * authentication schemes，e.g: Bearer
         * authenticationScheme: 'Bearer',
         */
        authenticationScheme: '',
        timeout: 300 * 1000,
        /*
         * 基础接口地址
         * baseURL: globSetting.apiUrl,
         */

        headers: { 'Content-Type': ContentTypeEnum.JSON, Authorization: 'Bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJodHRwczovL2Rldnd4LmJhaWppYXl1bi5jb20vYXBpL2FkbWluVXNlci9sb2dpbiIsImlhdCI6MTY5NzcwOTk4MiwiZXhwIjoxNzAwMzAxOTgyLCJuYmYiOjE2OTc3MDk5ODIsImp0aSI6IlJIc1NVb1MzUjhUWDFwS1MiLCJzdWIiOjEsInBydiI6Ijk4ZDFmNmU2YTdmODYxNDUyNWJlZTEzMDYwMjhiYjI0NjM5NWVmNmEiLCJyb2xlIjoiYWRtaW4ifQ.9atzdjPPt_uXAUt2HrGfSsLgyPnB5DkHgLaY4H-l604' },
        /*
         * 如果是form-data格式
         * headers: { 'Content-Type': ContentTypeEnum.FORM_URLENCODED },
         * 数据处理方式
         */
        transform: Object.assign({}, transform),
        // 配置项，下面的选项都可以在独立的接口请求中覆盖
        requestOptions: {
            // 默认将prefix 添加到url
            joinPrefix: true,
            // 是否返回原生响应头 比如：需要获取响应头时使用该属性
            isReturnNativeResponse: false,
            // 需要对返回数据进行处理
            isTransformResponse: true,
            // post请求的时候添加参数到url
            joinParamsToUrl: false,
            // 格式化提交参数时间
            formatDate: false,
            // 消息提示类型
            errorMessageMode: 'message',
            // 接口地址
            apiUrl: '',
            // 接口拼接地址
            urlPrefix: '',
            //  是否加入时间戳
            joinTime: false,
            // 忽略重复请求
            ignoreCancelToken: false,
            // 是否携带token
            withToken: true,
            retryRequest: {
                isOpenRetry: true,
                count: 5,
                waitTime: 100
            }
        }
    })
}

// Response result interceptor processing
axios.interceptors.response.use((res: AxiosResponse<any>) => {
    if (transform.responseInterceptors && isFunction(transform.responseInterceptors)) {
        res = transform.responseInterceptors(res)
    }
    return res
}, (error) => {
    return transform.responseInterceptorsCatch
                    && isFunction(transform.responseInterceptorsCatch)
                    && transform.responseInterceptorsCatch(error)
})

const defHttp = createAxios()
export default defHttp

export const post = defHttp.post.bind(defHttp) as typeof defHttp.post
export const postWithJson = defHttp.postWithJson.bind(defHttp) as typeof defHttp.postWithJson
export const get = defHttp.get.bind(defHttp) as typeof defHttp.get
export const put = defHttp.put.bind(defHttp) as typeof defHttp.put
export const del = defHttp.delete.bind(defHttp) as typeof defHttp.delete
export const form = defHttp.postForm.bind(defHttp) as typeof defHttp.postForm
export const uploadFile = defHttp.postForm.bind(defHttp) as typeof defHttp.uploadFile
/*
 * other api url
 * export const otherHttp = createAxios({
 *   requestOptions: {
 *     apiUrl: 'xxx',
 *     urlPrefix: 'xxx',
 *   },
 * });
 */
